import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_INPUT, type ClientMessage, type InputState, type RoomSnapshot, type ServerMessage } from '../shared/types';

type Status = 'offline' | 'connecting' | 'online' | 'reconnecting';
type Session = { code: string; token: string; playerId: string };
/**
 * What the voice controller listens to. The three server frames it owns, plus the transport
 * transitions it must react to — a dropped socket stops the microphone, a restored one lets
 * an opted-in courier rejoin — and the refusals the service sends for its own requests.
 */
export type VoiceEvent =
  | Extract<ServerMessage, { type: 'voice-state' | 'voice-ready' | 'voice-signal' }>
  | { type: 'voice-error'; code: string; message: string }
  | { type: 'voice-transport'; online: boolean };
export type VoiceListener = (event: VoiceEvent) => void;
type RoomActions = {
  begin: (message: ClientMessage) => void;
  send: (message: ClientMessage) => void;
  leave: () => void;
};

const SESSION_KEY = 'hearthhop.room.v1';
const SNAPSHOT_DEADLINE_MS = 1800;
const INPUT_ACK_DEADLINE_MS = 1800;
const WRITE_PROGRESS_DEADLINE_MS = 1800;
const WATCHDOG_INTERVAL_MS = 100;
const HANDSHAKE_DEADLINE_MS = 3000;
const PROBE_DEADLINE_MS = 1200;
const MAX_RETRY_DELAY_MS = 1200;
// Covers the normal 30 Hz stream at the acceptance RTT, but bounds a silent
// transport to eight unacknowledged frames instead of seconds of queued input.
const MAX_UNACKNOWLEDGED_INPUTS = 8;
const benignRaces = new Set(['STALE_EPOCH', 'STALE_INPUT', 'DUPLICATE_ACTION', 'RETRY_COOLDOWN']);
/** Voice refusals belong to the voice controller, not the room's error banner. */
const isVoiceError = (code: string) => code.startsWith('VOICE_');
const idleActions: RoomActions = { begin() {}, send() {}, leave() {} };

function readSession(): Session | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
    return value && typeof value.code === 'string' && typeof value.token === 'string' && typeof value.playerId === 'string' ? value : null;
  } catch { return null; }
}

function saveSession(session: Session | null): void {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch { /* Storage is optional for the current connection. */ }
}

function gameInputAllowed(snapshot: RoomSnapshot | null): boolean {
  const element = document.activeElement;
  return snapshot?.phase === 'playing' && !document.hidden
    && !document.querySelector('dialog[open], [role="dialog"]')
    && !(element instanceof HTMLElement && (element.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName)));
}

/** Same-origin, authenticated rooms. Only fresh snapshots make a socket playable. */
export function useRoom() {
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('offline');
  const [connectionMessage, setConnectionMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [latency, setLatency] = useState(0);
  const input = useRef<InputState>({ ...EMPTY_INPUT });
  const snapshotRef = useRef<RoomSnapshot | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const statusRef = useRef<Status>('offline');
  const actions = useRef<RoomActions>(idleActions);
  // Voice listeners live outside the transport effect: one controller spans reconnects.
  const voiceListeners = useRef(new Set<VoiceListener>());

  useEffect(() => {
    // Each effect mount owns its entire transport lifecycle. A StrictMode cleanup
    // or a late callback from a retired socket cannot act on its replacement.
    let disposed = false;
    let intentional = false;
    let pageSuspended = false;
    let generation = 0;
    let socket: WebSocket | null = null;
    let authenticatedSocket: WebSocket | null = null;
    let retryTimer: number | undefined;
    let handshakeTimer: number | undefined;
    let probeTimer: number | undefined;
    let inputTimer: number | undefined;
    let pingTimer: number | undefined;
    let probe: AbortController | null = null;
    let reconnectAttempt = 0;
    let lastSnapshotAt: number | null = null;
    let lastLatency = 0;
    let sequence = Date.now() * 100;
    let unacknowledged: { seq: number; sentAt: number }[] = [];
    let bufferedBytes = 0;
    let writeProgressAt: number | null = null;
    const keys = new Set<string>();
    let lastGamepad = { left: false, right: false, jump: false };
    let gamepadNeedsRelease = true;

    function neutralize() {
      keys.clear();
      input.current.left = input.current.right = input.current.jump = false;
      lastGamepad = { left: false, right: false, jump: false };
      gamepadNeedsRelease = true;
    }

    function publishVoice(event: VoiceEvent) {
      for (const listener of voiceListeners.current) listener(event);
    }

    function showStatus(next: Status, message: string | null = null) {
      if (disposed) return;
      // A transport that is no longer usable stops the microphone; a restored one lets an
      // opted-in courier rejoin. Voice follows the room's own view of the connection.
      if (next !== statusRef.current && (next === 'online' || statusRef.current === 'online')) {
        publishVoice({ type: 'voice-transport', online: next === 'online' });
      }
      statusRef.current = next;
      setStatus(next);
      setConnectionMessage(message);
    }

    function stopPumps() {
      window.clearInterval(inputTimer);
      window.clearInterval(pingTimer);
      inputTimer = pingTimer = undefined;
      authenticatedSocket = null;
      unacknowledged = [];
      bufferedBytes = 0;
      writeProgressAt = null;
      neutralize();
    }

    function cancelRetry() {
      window.clearTimeout(retryTimer);
      retryTimer = undefined;
    }

    function retireTransport() {
      stopPumps();
      window.clearTimeout(handshakeTimer);
      window.clearTimeout(probeTimer);
      handshakeTimer = probeTimer = undefined;
      probe?.abort();
      probe = null;
      const old = socket;
      socket = null;
      if (old) {
        old.onopen = old.onmessage = old.onclose = null;
        old.onerror = () => {};
        // No application send queue exists. The superseded connection is closed
        // and cannot publish frames or errors into the new authenticated session.
        old.close();
      }
    }

    function endSession(message: string | null) {
      intentional = true;
      generation++;
      cancelRetry();
      retireTransport();
      sessionRef.current = null;
      saveSession(null);
      snapshotRef.current = null;
      lastSnapshotAt = null;
      setSnapshot(null);
      setPlayerId(null);
      showStatus('offline');
      setError(message);
    }

    function reconnect(message = 'Connection interrupted. Reconnecting to your camp… Controls are paused.') {
      if (disposed || intentional || pageSuspended) return;
      if (!sessionRef.current) {
        endSession('We couldn’t reach the camp. Check your connection and try again.');
        return;
      }
      generation++;
      retireTransport();
      showStatus('reconnecting', message);
      setError(null);
      if (retryTimer !== undefined) return;
      const delay = Math.min(MAX_RETRY_DELAY_MS, 300 * 1.7 ** reconnectAttempt++);
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        connect();
      }, delay);
    }

    function isFresh() {
      if (disposed || intentional || pageSuspended || statusRef.current !== 'online') return false;
      const now = performance.now();
      if (socket && socket.readyState !== WebSocket.OPEN) {
        // readyState can change before the close event is dispatched. Pause now,
        // but preserve that event's terminal code (especially takeover 4000)
        // instead of retiring its handler and accidentally stealing the session.
        const closing = socket;
        stopPumps();
        showStatus('reconnecting', 'Connection interrupted. Checking your camp… Controls are paused.');
        handshakeTimer = window.setTimeout(() => {
          if (!disposed && !intentional && socket === closing) reconnect();
        }, HANDSHAKE_DEADLINE_MS);
        return false;
      }
      if (!socket || authenticatedSocket !== socket
        || lastSnapshotAt === null || now - lastSnapshotAt >= SNAPSHOT_DEADLINE_MS) {
        reconnect('The connection went quiet. Reconnecting to your camp… Controls are paused.');
        return false;
      }
      // An open socket can receive fresh worlds while its uplink is stranded.
      // Only an actual acknowledgement removes an input (below); merely receiving
      // a snapshot, reaching the cap, or restoring the link never renews its age.
      const oldest = unacknowledged[0];
      if (oldest && now - oldest.sentAt >= INPUT_ACK_DEADLINE_MS) {
        reconnect('Your controls lost their connection. Reconnecting to your camp… Controls are paused.');
        return false;
      }
      // A browser write queue can also stall before any input is sent. Observe
      // genuine byte drainage independently of both snapshots and input ACKs.
      const queued = socket.bufferedAmount;
      if (queued === 0) writeProgressAt = null;
      else {
        if (writeProgressAt === null || queued < bufferedBytes) writeProgressAt = now;
        if (now - writeProgressAt >= WRITE_PROGRESS_DEADLINE_MS) {
          reconnect('Your connection has stopped sending. Reconnecting to your camp… Controls are paused.');
          return false;
        }
      }
      bufferedBytes = queued;
      return true;
    }

    function send(message: ClientMessage) {
      if (!isFresh() || !socket) return;
      // Do not append another frame while the browser still has bytes queued.
      // A skipped input is never retained for later replay; the next frame samples
      // current intent, and reconnect always begins with neutral intent.
      if (socket.bufferedAmount > 0) return;
      try { socket.send(JSON.stringify(message)); }
      catch { reconnect(); }
    }

    function transmitInput() {
      if (!isFresh()) return;
      const current = snapshotRef.current;
      if (current?.phase !== 'playing' || !current.world || !sessionRef.current || !socket) return;
      if (!gameInputAllowed(current)) neutralize();
      if (socket.bufferedAmount > 0 || unacknowledged.length >= MAX_UNACKNOWLEDGED_INPUTS) return;
      sequence = Math.max(sequence + 1, Date.now() * 100);
      input.current.seq = sequence;
      try {
        socket.send(JSON.stringify({ type: 'input', input: { ...input.current }, epoch: current.world.epoch } satisfies ClientMessage));
        unacknowledged.push({ seq: sequence, sentAt: performance.now() });
      } catch { reconnect(); }
    }

    function startPumps() {
      if (inputTimer !== undefined) return;
      inputTimer = window.setInterval(transmitInput, 1000 / 30);
      pingTimer = window.setInterval(() => send({ type: 'ping', time: performance.now() }), 2000);
      send({ type: 'ping', time: performance.now() });
    }

    function connect(initial?: ClientMessage) {
      if (disposed || pageSuspended || (!initial && (intentional || !sessionRef.current))) return;
      intentional = false;
      cancelRetry();
      const attempt = ++generation;
      retireTransport();
      const saved = sessionRef.current;
      const isResume = !initial && Boolean(saved);
      const first = initial ?? (saved ? { type: 'resume', code: saved.code, token: saved.token } as const : null);
      const currentAttempt = () => !disposed && !intentional && !pageSuspended && generation === attempt;
      // Keep an outage's explanation stable through backoff; repeatedly swapping
      // it for a generic retry message would churn the shell's live region.
      if (!isResume || statusRef.current !== 'reconnecting') {
        showStatus(isResume ? 'reconnecting' : 'connecting', isResume
          ? navigator.onLine ? 'Reconnecting to your camp… Controls are paused.' : 'You’re offline. Reconnecting when your connection returns. Controls are paused.'
          : null);
      }

      function openSocket() {
        if (!currentAttempt() || !first) return;
        const url = new URL('/ws', window.location.href);
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        let ws: WebSocket;
        try { ws = new WebSocket(url); }
        catch { reconnect(); return; }
        socket = ws;
        let welcomed = false;
        const currentSocket = () => currentAttempt() && socket === ws;
        // OPEN alone is insufficient: welcome AND a fresh snapshot must arrive.
        handshakeTimer = window.setTimeout(() => {
          if (currentSocket()) reconnect('The camp is taking a moment to answer. Reconnecting… Controls are paused.');
        }, HANDSHAKE_DEADLINE_MS);
        ws.onopen = () => {
          if (!currentSocket()) return;
          try { ws.send(JSON.stringify(first)); }
          catch { reconnect(); }
        };
        ws.onmessage = event => {
          if (!currentSocket() || ws.readyState !== WebSocket.OPEN) return;
          let message: ServerMessage;
          try { message = JSON.parse(event.data); } catch { return; }
          if (message.type === 'welcome') {
            if (isResume && saved && (message.playerId !== saved.playerId || message.code !== saved.code)) {
              endSession('Your saved camp could not be restored. Please ask your friends for a fresh invitation.');
              return;
            }
            welcomed = true;
            sessionRef.current = { code: message.code, token: message.token, playerId: message.playerId };
            saveSession(sessionRef.current);
            setPlayerId(message.playerId);
            neutralize();
            if (isResume) setConnectionMessage('Camp found. Waiting for a fresh trail… Controls are paused.');
          } else if (message.type === 'snapshot') {
            const next = message.snapshot;
            const session = sessionRef.current;
            if (!welcomed || !session || next.code !== session.code
              || !next.players.some(player => player.id === session.playerId && player.connected)) return;
            const prior = snapshotRef.current;
            if (prior?.code === next.code && next.revision <= prior.revision) return;
            if (prior?.world?.epoch !== next.world?.epoch || next.phase !== 'playing') {
              neutralize();
              unacknowledged = [];
            }
            lastSnapshotAt = performance.now();
            snapshotRef.current = next;
            setSnapshot(next);
            const self = next.world?.players.find(player => player.id === session.playerId);
            if (self) {
              sequence = Math.max(sequence, self.lastInput);
              unacknowledged = unacknowledged.filter(pending => pending.seq > self.lastInput);
            }
            if (authenticatedSocket !== ws) {
              neutralize();
              authenticatedSocket = ws;
              window.clearTimeout(handshakeTimer);
              handshakeTimer = undefined;
              reconnectAttempt = 0;
              showStatus('online');
              setError(null);
              startPumps();
            }
          } else if (message.type === 'voice-state' || message.type === 'voice-ready' || message.type === 'voice-signal') {
            // Only the authenticated socket's voice traffic is honoured; a retired
            // connection cannot push state into the controller that replaced it.
            if (authenticatedSocket === ws) publishVoice(message);
          } else if (message.type === 'pong') {
            if (authenticatedSocket !== ws || !Number.isFinite(message.time)) return;
            lastLatency = Math.max(0, Math.round(performance.now() - message.time));
            setLatency(lastLatency);
          } else if (message.type === 'error') {
            if (isVoiceError(message.code) || message.code === 'VOICE_REJECTED') {
              publishVoice({ type: 'voice-error', code: message.code, message: message.message });
              return;
            }
            if (benignRaces.has(message.code)) return;
            if (message.code === 'WRONG_PHASE' && message.message.startsWith('Movement')) return;
            if (isResume && ['ROOM_NOT_FOUND', 'INVALID_TOKEN', 'NOT_IN_ROOM'].includes(message.code)) {
              endSession(message.message);
            } else if (isResume && authenticatedSocket !== ws && message.code === 'RATE_LIMITED') {
              reconnect('The camp is busy. Reconnecting in a moment… Controls are paused.');
            } else if (!sessionRef.current) {
              endSession(message.message);
            } else setError(message.message);
          }
        };
        ws.onerror = () => { /* The close handler owns the single retry path. */ };
        ws.onclose = event => {
          if (!currentSocket()) return;
          if ([4000, 4001, 4003, 1011, 1008].includes(event.code)) {
            // In particular, 4000 is a deliberate session takeover. Retrying would
            // steal it back and create a perpetual fight between two tabs.
            endSession(event.reason || 'This camp has closed. You can make a new one.');
          } else reconnect();
        };
      }

      if (!isResume) { openSocket(); return; }
      // navigator.onLine is only a hint. A cancellable same-origin health request
      // avoids piling up WebSocket handshakes while an internet connection is down.
      const controller = new AbortController();
      probe = controller;
      probeTimer = window.setTimeout(() => controller.abort(), PROBE_DEADLINE_MS);
      void fetch('/api/health', { cache: 'no-store', signal: controller.signal }).then(response => {
        if (!currentAttempt()) return;
        window.clearTimeout(probeTimer);
        probeTimer = undefined;
        probe = null;
        if (!response.ok) {
          reconnect('The camp is temporarily unavailable. Reconnecting… Controls are paused.');
          return;
        }
        openSocket();
      }).catch(() => {
        if (currentAttempt()) reconnect('Can’t reach the camp. Check your internet connection; we’ll keep trying. Controls are paused.');
      });
    }

    const ownedActions: RoomActions = {
      begin(message) {
        if (disposed || sessionRef.current || statusRef.current === 'connecting') return;
        setError(null);
        snapshotRef.current = null;
        lastSnapshotAt = null;
        setSnapshot(null);
        connect(message);
      },
      send,
      leave() {
        if (disposed) return;
        if (socket?.readyState === WebSocket.OPEN && authenticatedSocket === socket
          && lastSnapshotAt !== null && performance.now() - lastSnapshotAt < SNAPSHOT_DEADLINE_MS && socket.bufferedAmount === 0) {
          socket.send(JSON.stringify({ type: 'leave' } satisfies ClientMessage));
        }
        endSession(null);
        const url = new URL(window.location.href);
        url.searchParams.delete('room');
        window.history.replaceState(null, '', url);
      },
    };
    actions.current = ownedActions;

    const movementKeys = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'KeyA', 'KeyD', 'KeyW', 'Space']);
    const applyKeys = () => {
      input.current.left = keys.has('ArrowLeft') || keys.has('KeyA') || lastGamepad.left;
      input.current.right = keys.has('ArrowRight') || keys.has('KeyD') || lastGamepad.right;
      input.current.jump = keys.has('Space') || keys.has('ArrowUp') || keys.has('KeyW') || lastGamepad.jump;
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isFresh() || !gameInputAllowed(snapshotRef.current)) return;
      if (movementKeys.has(event.code)) {
        event.preventDefault();
        if (!event.repeat) { keys.add(event.code); applyKeys(); transmitInput(); }
      } else if (event.code === 'KeyR' && !event.repeat && snapshotRef.current?.world) {
        event.preventDefault();
        neutralize();
        send({ type: 'retry', epoch: snapshotRef.current.world.epoch });
      } else if (['Digit1', 'Digit2', 'Digit3'].includes(event.code) && !event.repeat) {
        send({ type: 'emote', emote: event.code === 'Digit1' ? 'hello' : event.code === 'Digit2' ? 'here' : 'heart' });
      }
    };
    const keyUp = (event: KeyboardEvent) => {
      if (movementKeys.has(event.code) && keys.delete(event.code)) { applyKeys(); transmitInput(); }
    };
    const blur = () => { neutralize(); transmitInput(); };
    const visibility = () => {
      neutralize();
      // Also checks freshness immediately when a throttled/background tab returns.
      if (isFresh()) transmitInput();
    };
    const offline = () => {
      if (sessionRef.current || socket || probe) reconnect('You’re offline. Reconnecting when your connection returns. Controls are paused.');
    };
    const online = () => {
      if (disposed || intentional || pageSuspended || !sessionRef.current || statusRef.current !== 'reconnecting') return;
      // Accelerate an existing retry, never create a second attempt or pump.
      if (retryTimer !== undefined) {
        cancelRetry();
        connect();
      }
    };
    const pageHide = () => {
      pageSuspended = true;
      generation++;
      cancelRetry();
      retireTransport();
      if (sessionRef.current) showStatus('reconnecting', 'Reconnecting to your camp… Controls are paused.');
    };
    const pageShow = () => {
      if (!pageSuspended) return;
      pageSuspended = false;
      if (sessionRef.current && !intentional) connect();
    };
    const watchdog = window.setInterval(() => {
      if (statusRef.current === 'online') isFresh();
    }, WATCHDOG_INTERVAL_MS);

    let gamepadFrame = 0;
    const gamepadLoop = () => {
      const pad = navigator.getGamepads?.().find(pad => pad?.connected);
      const raw = {
        left: Boolean(pad && (pad.axes[0] < -0.3 || pad.buttons[14]?.pressed)),
        right: Boolean(pad && (pad.axes[0] > 0.3 || pad.buttons[15]?.pressed)),
        jump: Boolean(pad?.buttons[0]?.pressed),
      };
      const allowed = isFresh() && gameInputAllowed(snapshotRef.current);
      if (allowed && gamepadNeedsRelease && !raw.left && !raw.right && !raw.jump) gamepadNeedsRelease = false;
      const enabled = allowed && !gamepadNeedsRelease;
      const next = { left: enabled && raw.left, right: enabled && raw.right, jump: enabled && raw.jump };
      if (next.left !== lastGamepad.left || next.right !== lastGamepad.right || next.jump !== lastGamepad.jump) {
        lastGamepad = next;
        applyKeys();
        transmitInput();
      }
      gamepadFrame = requestAnimationFrame(gamepadLoop);
    };
    gamepadFrame = requestAnimationFrame(gamepadLoop);
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    window.addEventListener('blur', blur);
    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    window.addEventListener('pagehide', pageHide);
    window.addEventListener('pageshow', pageShow);
    document.addEventListener('visibilitychange', visibility);

    // Read-only inspection, with no token, socket, send method, or timer handles.
    const inspect = () => ({
      snapshot: snapshotRef.current, playerId: sessionRef.current?.playerId ?? null,
      status: statusRef.current, latency: lastLatency, input: { ...input.current },
      snapshotAgeMs: lastSnapshotAt === null ? null : Math.round(performance.now() - lastSnapshotAt),
      inputPaused: inputTimer === undefined, unacknowledgedInputs: unacknowledged.length,
      oldestUnacknowledgedInputAgeMs: unacknowledged.length ? Math.round(performance.now() - unacknowledged[0].sentAt) : null,
      writeStallAgeMs: writeProgressAt === null ? null : Math.round(performance.now() - writeProgressAt),
      bufferedBytes,
    });
    Object.defineProperty(window, '__hearthhop', { configurable: true, get: inspect });
    sessionRef.current = readSession();
    if (sessionRef.current) connect();
    else showStatus('offline');

    return () => {
      disposed = true;
      intentional = true;
      generation++;
      cancelRetry();
      retireTransport();
      window.clearInterval(watchdog);
      cancelAnimationFrame(gamepadFrame);
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      window.removeEventListener('blur', blur);
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
      window.removeEventListener('pagehide', pageHide);
      window.removeEventListener('pageshow', pageShow);
      document.removeEventListener('visibilitychange', visibility);
      if (actions.current === ownedActions) actions.current = idleActions;
      if (Object.getOwnPropertyDescriptor(window, '__hearthhop')?.get === inspect) Reflect.deleteProperty(window, '__hearthhop');
    };
  }, []);

  const send = useCallback((message: ClientMessage) => actions.current.send(message), []);
  const create = useCallback((name: string, color: number) => actions.current.begin({ type: 'create', name, color }), []);
  const join = useCallback((code: string, name: string, color: number) => actions.current.begin({ type: 'join', code: code.trim().toUpperCase(), name, color }), []);
  const leave = useCallback(() => actions.current.leave(), []);
  const clearError = useCallback(() => setError(null), []);
  const onVoice = useCallback((listener: VoiceListener) => {
    voiceListeners.current.add(listener);
    return () => { voiceListeners.current.delete(listener); };
  }, []);
  // Read synchronously: a voice listener runs during the transport transition that caused it,
  // before React has re-rendered, so `status` from this render would still be the old value.
  const isOnline = useCallback(() => statusRef.current === 'online', []);
  return { snapshot, playerId, status, connectionMessage, error, latency, input, send, create, join, leave, clearError, onVoice, isOnline };
}
